/**
 * Onboarding Wrapper
 *
 * Wraps the main content area to provide:
 * 1. Auto-opens the onboarding panel on first visit (replaces the old welcome screen overlay)
 * 2. Trial expired overlay — blocks access when trial ends
 *
 * The onboarding checklist and trial banner are in the right-side
 * panel (OnboardingPanelContent) accessible via the "Onboarding" nav item.
 */

import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCorporateWithStore } from '_stores/corporate.store'
import { useMe } from '_hooks/me.hooks'
import { useTrialStatus } from '_hooks/use-trial-management.hooks'
import { useOnboardingStore } from '_stores/onboarding.store'
import Button from '@hapana/shared/web/components/shared/button/button.component.jsx'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
} from '@hapana/shared/web/components/shared/button/button.constants'
import {
  trackWelcomeScreenViewed,
  trackTrialExpiredWallShown,
} from '@hapana/shared/analytics/src/tracking-functions'

import { useSupportAssistant } from '../support-assistant/support-assistant.hooks'
import { styles } from './onboarding-wrapper.component.styles'

import { useEffect, useRef } from 'react'

interface OnboardingWrapperProps {
  children: React.ReactNode
}

const TRIAL_STATUSES_WITH_ONBOARDING = ['trial_active', 'trial_expiring', 'trial_expired']

const OnboardingWrapper = ({ children }: OnboardingWrapperProps) => {
  const { t } = useTranslation('core')
  const {
    setIsOpen,
    setActivePanel,
    setActiveTab,
    setPrefillIssueType,
    setPrefillDescription,
    isOpen,
  } = useSupportAssistant()
  const { corporate } = useCorporateWithStore()
  const corporateId = corporate?.corporateBySubdomain?.corporateId

  const { data: trialData, isLoading } = useTrialStatus(corporateId)
  const { welcomeDismissed, dismissWelcome } = useOnboardingStore()
  const hasTrackedWelcome = useRef(false)
  const hasTrackedExpiredWall = useRef(false)

  const isTrialUser =
    trialData?.trialStatus && TRIAL_STATUSES_WITH_ONBOARDING.includes(trialData.trialStatus)

  const isTrialExpired = trialData?.trialStatus === 'trial_expired'

  // On first visit: auto-dismiss the welcome screen and open the onboarding panel
  // so prospects land on the home page with the onboarding checklist visible.
  useEffect(() => {
    if (!isLoading && isTrialUser && !welcomeDismissed && corporateId) {
      if (!hasTrackedWelcome.current) {
        trackWelcomeScreenViewed({ corporate_id: corporateId })
        hasTrackedWelcome.current = true
      }
      dismissWelcome()
      if (!isOpen) {
        setActivePanel('onboarding')
        setIsOpen(true)
      }
    }
  }, [
    isLoading,
    isTrialUser,
    welcomeDismissed,
    corporateId,
    isOpen,
    dismissWelcome,
    setActivePanel,
    setIsOpen,
  ])

  // Track expired wall view
  useEffect(() => {
    if (isTrialExpired && corporateId && !hasTrackedExpiredWall.current) {
      trackTrialExpiredWallShown({ corporate_id: corporateId })
      hasTrackedExpiredWall.current = true
    }
  }, [isTrialExpired, corporateId])

  return (
    <>
      {/* Trial expired overlay */}
      {!isLoading && isTrialExpired && (
        <Box sx={styles.overlay}>
          <Box sx={styles.dialog}>
            <Typography typography="header2" color="gray.100" sx={styles.titleSpacing}>
              {t('onboarding.trialExpired.title')}
            </Typography>
            <Typography typography="medium" color="gray.64" sx={styles.bodySpacing}>
              {t('onboarding.trialExpired.body')}
            </Typography>
            <Box sx={styles.buttonRow}>
              <Button
                variant={BUTTON_VARIANTS.CONTAINED}
                theme={BUTTON_THEME.PRIMARY}
                label={t('onboarding.trialExpired.viewPlans')}
                onClick={() => {
                  setPrefillIssueType('PLANS')
                  setPrefillDescription(t('onboarding.trialExpired.viewPlansPrefill'))
                  setActivePanel('assistant')
                  setActiveTab('support')
                  setIsOpen(true)
                }}
              />
              <Button
                variant={BUTTON_VARIANTS.OUTLINED}
                theme={BUTTON_THEME.GRAY}
                label={t('onboarding.trialExpired.contactSales')}
                onClick={() => window.open('https://hapana.com/contact-sales', '_blank')}
              />
            </Box>
          </Box>
        </Box>
      )}

      {children}
    </>
  )
}

export default OnboardingWrapper
